"use client";

import * as React from "react";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { icons } from "../../assets/icons";
import { loginAndGetUnidades } from "../../api/authService";

export default function AuthForm() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [formData, setFormData] = React.useState({
    email: "",
    password: "",
  });
  const [formErrors, setFormErrors] = React.useState({
    email: "",
    password: "",
  });

  const validateForm = () => {
    const errors = { email: "", password: "" };

    if (!formData.email) {
      errors.email = "El correo electrónico es obligatorio";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "El formato del correo electrónico no es válido";
    }

    if (!formData.password) {
      errors.password = "La contraseña es obligatoria";
    } else if (formData.password.length < 4) {
      errors.password = "La contraseña debe tener al menos 4 caracteres";
    }

    setFormErrors(errors);
    return !errors.email && !errors.password;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) return;

    try {
      setError(null);
      setIsSubmitting(true);

      const { envelope, unidades } = await loginAndGetUnidades({
        email: formData.email,
        contrasena: formData.password,
      });

      if (!Array.isArray(envelope.object_)) {
        throw new Error("Respuesta inesperada del servidor");
      }

      if (unidades.length === 0) {
        setError(
          "Tu usuario no tiene unidades asignadas. Contacta al administrador del sistema."
        );
        return;
      }

      // Extraer información del usuario del primer objeto de respuesta
      const userData = envelope.object_[0];
      const usuarioInfo = {
        codigo: userData.usu_codigo,
        email: userData.usu_email,
        nombre: userData.usu_nombre_completo || "error", // Fallback to email
        nombreCompleto: userData.usu_nombre_completo || userData.usu_email,
        numeroCelular: userData.usu_numero_celular,
        master: userData.usu_master,
      };

      // Navegar a la selección de unidad con información del usuario
      navigate("/UnitSelectionPage", {
        state: {
          email: formData.email,
          password: formData.password, // Guardamos la contraseña para usarla después
          usuario: usuarioInfo,
          token: envelope.token_,
          unidades,
        },
      });
    } catch (e: any) {
      console.log("Error completo:", e); // Para debugging

      let errorMessage = "Error al intentar iniciar sesión";

      // Verificar si es un error de API con mensaje del servidor
      if (e?.serverMessage) {
        errorMessage = e.serverMessage;
      } else if (e?.message) {
        errorMessage = e.message;
      } else if (e?.response?.data?.message) {
        errorMessage = e.response.data.message;
      } else if (e?.response?.statusText) {
        errorMessage = e.response.statusText;
      } else if (typeof e === "string") {
        errorMessage = e;
      }

      // Solo personalizar mensajes si no vienen del servidor
      if (!e?.serverMessage && !e?.message?.includes("contrasena")) {
        if (
          errorMessage.toLowerCase().includes("unauthorized") ||
          errorMessage.toLowerCase().includes("401")
        ) {
          errorMessage =
            "Credenciales incorrectas. Verifica tu correo y contraseña.";
        } else if (
          errorMessage.toLowerCase().includes("network") ||
          errorMessage.toLowerCase().includes("fetch")
        ) {
          errorMessage = "Error de conexión. Verifica tu conexión a internet.";
        } else if (errorMessage.toLowerCase().includes("timeout")) {
          errorMessage = "La solicitud tardó demasiado. Inténtalo nuevamente.";
        }
      }

      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-100 via-gray-200 to-gray-300 px-4">
      <Card
        className="w-full max-w-md shadow-xl animate-slideUp border rounded-2xl"
        style={{ backgroundColor: "rgb(247,247,247)" }}
      >
        <CardContent>
          <div className="flex justify-center mb-6">
            <img
              src={icons.logo}
              alt="App Logo"
              className="w-40 h-auto object-contain"
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-col space-y-2">
              <Label htmlFor="email" className="text-gray-700">
                Correo electrónico
              </Label>
              <Input
                id="email"
                placeholder="usuario@easyph.com"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="bg-white text-gray-900 border border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg"
              />
              {formErrors.email && (
                <p className="text-sm text-red-600">{formErrors.email}</p>
              )}
            </div>

            <div className="flex flex-col space-y-2">
              <Label htmlFor="password" className="text-gray-700">
                Contraseña
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="********"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="bg-white text-gray-900 border border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-military-600 transition"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {formErrors.password && (
                <p className="text-sm text-red-600">{formErrors.password}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-military-500 hover:bg-military-600 text-white shadow-md rounded-lg transition disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                "Iniciar Sesión"
              )}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-sm text-gray-500">
            ¿Olvidaste tu contraseña?
            <Button
              variant="link"
              onClick={() => navigate("/recover-password-step1")}
              className="pl-1 text-military-600 hover:text-military-500"
            >
              Recuperar
            </Button>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}